import { calculateAdjustments } from "../adjustments";
import { laws } from "@/data-source";
import { calculateInsuranceWage } from "../insuranceWage";
import { calculateSalaryTax } from "../salaryTax";
import {
    calculateSocialInsurance,
    calculateSocialInsuranceEmployerShare,
} from "../socialInsurance";
import type { VariantCalculations } from "@/types/salary";
import { DEFAULT_LAW_YEAR } from "@/constants/salary";
import type { CalculateNetSalaryParams } from "./types";

const calculateNetSalary = ({
    grossSalary,
    insuranceWage,
    bonus = [],
    deduction = [],
    isInsured = true,
    lawYear = DEFAULT_LAW_YEAR,
}: CalculateNetSalaryParams): [number, VariantCalculations] => {
    if (!laws[lawYear]) throw new RangeError("Unsupported law year");
    if (
        ![grossSalary].every((value) => Number.isFinite(value) && value >= 0) ||
        (insuranceWage !== undefined &&
            (!Number.isFinite(insuranceWage) || insuranceWage < 0))
    )
        throw new RangeError(
            "Salary, adjustments and insurance wage must be finite and nonnegative"
        );
    const adjustment = calculateAdjustments(bonus, deduction);
    grossSalary += adjustment.earnings;
    if (
        !Number.isFinite(grossSalary) ||
        grossSalary < 0 ||
        !Number.isFinite(grossSalary + adjustment.cash) ||
        grossSalary + adjustment.cash < 0
    )
        throw new RangeError(
            "Adjusted gross salary must be finite and nonnegative"
        );

    insuranceWage = isInsured
        ? insuranceWage ?? calculateInsuranceWage(grossSalary, lawYear)
        : 0;

    const socialInsuranceWorkerShare = calculateSocialInsurance(insuranceWage);

    const socialInsuranceEmployerShare =
        calculateSocialInsuranceEmployerShare(insuranceWage);

    const martyrsFamiliesFunds = grossSalary * 0.0005;

    const salaryTax = calculateSalaryTax({
        grossSalary,
        socialInsuranceWorkerShare,
        lawYear,
    });

    const totalDeduction =
        socialInsuranceWorkerShare + martyrsFamiliesFunds + salaryTax;

    const netSalary = grossSalary - totalDeduction + adjustment.cash;

    return [
        netSalary,
        {
            insuranceWage,
            socialInsuranceWorkerShare,
            salaryTax,
            martyrsFamiliesFunds,
            socialInsuranceEmployerShare,
            totalDeduction,
        },
    ];
};

export { calculateNetSalary };
export type { CalculateNetSalaryParams };
